import React, {
    useState,
    useCallback,
    useEffect,
    createContext,
    useContext,
    ReactNode,
} from 'react';

// Define interface for menu data
export interface MenuData {
    position?: { x: number; y: number };
    data?: any;
    id?: string;
    menuItems?: Array<{ label: string; action: () => void }>;
    x?: number;
    y?: number;
    [key: string]: any; // To allow flexibility for any additional properties
}

// Define the context interface
interface ContextMenuContextType {
    openContextMenu: (menuData: MenuData) => void;
    closeAllContextMenus: () => void;
    activeContextMenu: MenuData | null;
}

// Create a shared context for managing context menus across components
export const ContextMenuContext = createContext<ContextMenuContextType>({
    openContextMenu: () => {},
    closeAllContextMenus: () => {},
    activeContextMenu: null,
});

// Custom hook for using the context menu
export const useContextMenu = (): ContextMenuContextType =>
    useContext(ContextMenuContext);

// Props interface for the provider
interface ContextMenuProviderProps {
    children: ReactNode;
}

// Context menu provider to ensure only one context menu is open at a time
export const ContextMenuProvider: React.FC<ContextMenuProviderProps> = ({
    children,
}) => {
    const [activeContextMenu, setActiveContextMenu] = useState<MenuData | null>(
        null,
    );

    const openContextMenu = useCallback((menuData: MenuData) => {
        // Prevent event bubbling that could trigger immediate close
        if (menuData.event) {
            menuData.event.preventDefault();
            menuData.event.stopPropagation();
            // Remove event from data to avoid circular structure
            delete menuData.event;
        }

        // Close any existing context menu first
        setActiveContextMenu(menuData);
    }, []);

    const closeAllContextMenus = useCallback(() => {
        setActiveContextMenu(null);
    }, []);

    // Close context menu when clicking outside or pressing escape
    useEffect(() => {
        if (activeContextMenu) {
            const handleOutsideClick = (e: MouseEvent): void => {
                // Don't close if this is a contextmenu event (right click)
                if (e.type === 'contextmenu') {
                    return;
                }

                // Check if the click was inside the context menu
                const contextMenuElement =
                    document.getElementById('context-menu');
                if (
                    contextMenuElement &&
                    contextMenuElement.contains(e.target as Node)
                ) {
                    return; // Don't close if clicked inside menu
                }

                closeAllContextMenus();
            };

            const handleEscape = (e: KeyboardEvent): void => {
                if (e.key === 'Escape') closeAllContextMenus();
            };

            // Use capture phase to handle events before they reach other handlers
            document.addEventListener('click', handleOutsideClick, true);
            document.addEventListener('keydown', handleEscape, true);

            // IMPORTANT: Remove this handler that blocks all context menu events
            // document.addEventListener('contextmenu', handleOutsideClick);

            return () => {
                document.removeEventListener('click', handleOutsideClick, true);
                document.removeEventListener('keydown', handleEscape, true);
                // document.removeEventListener('contextmenu', handleOutsideClick);
            };
        }
    }, [activeContextMenu, closeAllContextMenus]);

    return (
        <ContextMenuContext.Provider
            value={{
                openContextMenu,
                closeAllContextMenus,
                activeContextMenu,
            }}
        >
            {children}
        </ContextMenuContext.Provider>
    );
};
